import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Language, LanguageService } from '@lx/language';
import { ProductPackage, ProductPackageEnum } from '@lx/quote-tool/shared/types';
import { DestinationControllerApiService } from '@lx/shared/api';
import { CountryDataService } from '@lx/shared/data-access/destination';
import { ProductSchoolsService, SchoolV4 } from '@lx/shared/data-access/school';
import { Country } from '@lx/shared/data-access/types';
import { getLocale } from '@lx/shared/i18n';
import { getCompany, getCompanyHeader } from '@lx/shared/tokens';
import { COMPANIES } from '@lx/shared/types';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { AsyncSubject, combineLatest, map, Observable, switchMap, tap } from 'rxjs';
import { CountryWeight } from '../../types';
import { SharedDataState } from './shared-data.state';
import { AyaLanguageService } from '../../services/aya-language.service';
import { Flow, Flows } from '@lx/quote-tool/lite/shared/types';

@Injectable({ providedIn: 'root' })
export class SharedDataStore extends ComponentStore<SharedDataState> {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly company = getCompany();
  private readonly languageService = inject(LanguageService);
  private readonly productSchoolsService = inject(ProductSchoolsService);
  private readonly countryDataService = inject(CountryDataService);
  private readonly destinationService = inject(DestinationControllerApiService);
  private readonly ayaLanguageService = inject(AyaLanguageService);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super({});
  }

  // Selectors
  readonly languages$ = this.select(({ languages }) => languages);
  readonly countries$ = this.select(({ countries }) => countries);
  readonly countryWeights$ = this.select(({ countryWeights }) => countryWeights);
  readonly schools$ = this.select(({ schools }) => schools);
  readonly residenceCountries$ = this.select(({ residenceCountries }) => residenceCountries);
  readonly suggestedPremiumActivityCode$ = this.select(({ suggestedPremiumActivityCode }) => suggestedPremiumActivityCode);

  // Updaters
  readonly updateState = this.updater((state, partialState: Partial<SharedDataState>) => ({
    ...state,
    ...partialState,
  }));

  // Effects
  readonly updateCourseLanguages = this.effect((activeFlow$: Observable<Flow>) => {
    return activeFlow$.pipe(
      switchMap((flow) => {
        if (flow === Flows.Aya) {
          return this.ayaLanguageService.getLanguages();
        }
        return this.languageService.getByProductPackage(this.getProductPackageForFlow(flow));
      }),
      tapResponse(
        (languages: Language[]) => {
          const sortedLanguages = [...languages].sort((a, b) => a.weight - b.weight);

          this.setState((state) => ({
            ...state,
            languages: sortedLanguages,
          }));
        },
        () => this.setState((state) => ({ ...state })),
      ),
    );
  });

  /**
   * Gets the default language from the currently loaded languages in the store.
   * @returns Observable<string> - The default language code
   */
  getDefaultLanguage(): Observable<string> {
    return this.languages$.pipe(map((languages) => this.getMainLanguage(languages)));
  }

  /**
   * Loads the languages for a specific flow and updates the store state.
   * This method is responsible only for fetching and storing languages.
   *
   * @param flow - The flow for which to load languages
   * @returns Observable<Language[]> - The loaded languages for the specified flow
   */
  loadLanguagesForFlow(flow: Flow): Observable<Language[]> {
    const languagesSource$ =
      flow === Flows.Aya ? this.ayaLanguageService.getLanguages() : this.languageService.getByProductPackage(this.getProductPackageForFlow(flow));

    return languagesSource$.pipe(
      map((languages) => [...languages].sort((a, b) => a.weight - b.weight)),
      tap((sortedLanguages: Language[]) => {
        this.updateState({ languages: sortedLanguages });
      }),
    );
  }

  /**
   * Loads the shared data (languages, schools, countries) and updates the state with the loaded data.
   *
   * Returns an observable and when the data is loaded, it completes the observable.
   *
   * This method is used to load the shared data when the application starts.
   */
  loadSharedData() {
    const loadedData$ = new AsyncSubject();

    // Alpadia starts as junior by default
    const defaultProductPackage = this.company === COMPANIES.ALPADIA ? ProductPackageEnum.YOUNG_LEARNER_CAMPS : null;

    combineLatest([
      this.languageService.getByProductPackage(defaultProductPackage),
      this.productSchoolsService.getSchoolsV4(),
      this.countryDataService.getAllCountriesWithParameters({}),
      this.destinationService.getQuoteToolCountryWeights(this.locale, this.companyHeader),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([languages, schools, countries, countryWeights]: [Language[], SchoolV4[], Country[], CountryWeight[]]) => {
        const sortedLanguages = [...languages].sort((a, b) => a.weight - b.weight);
        this.updateState({ languages: sortedLanguages, schools, countries, countryWeights });
        loadedData$.next(true);
        loadedData$.complete();
      });
    return loadedData$.asObservable();
  }

  private getMainLanguage(languages: Language[]): string {
    if (!languages?.length) {
      throw new Error('Unable to determine main language: no languages available.');
    }
    const mainLanguage = languages.find(({ mainLanguage }) => mainLanguage);
    return mainLanguage ? mainLanguage.code : languages[0].code;
  }

  /**
   * Maps a flow type to its corresponding product package.
   * @param flow - The flow type
   * @returns The corresponding ProductPackage or null for default
   */
  private getProductPackageForFlow(flow: Flow): ProductPackage | null {
    return flow === Flows.Junior ? ProductPackageEnum.YOUNG_LEARNER_CAMPS : null;
  }
}
